import {makeStyles} from "tss-react/mui";
import {sidebarWidth, sidebarClass} from "../../resources/environment";

export const useSidebarStyles = makeStyles()((theme, {newSidebarDesign, showSidebarText} = {}) => ({
  root: {
    display: "flex",
  },
  sidebarContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
  overviewPaper: {
    position: "fixed",
    bottom: 0,
    left: 0,
    margin: 0,
    padding: 0,
    height: theme.palette.layout.overview.height,
    width: newSidebarDesign ?
      showSidebarText ?
        sidebarWidth.open :
        sidebarWidth.collapsed :
      sidebarWidth.open,
    transition: newSidebarDesign ?
      theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }) :
      undefined,
    [`&.${sidebarClass}`]: {
      width: sidebarWidth.collapsed,
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
    boxShadow: `${theme.palette.layout.overview.boxShadow} !important`,
    "@media print": {
      display: "none",
    },
  },
  drawer: {
    zIndex: theme.zIndex.drawer + 1,
    width: newSidebarDesign ?
      showSidebarText ?
        sidebarWidth.open :
        sidebarWidth.collapsed :
      sidebarWidth.open,
    flexShrink: 0,
    transition: newSidebarDesign ?
      theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }) :
      undefined,
    [`&.${sidebarClass}`]: {
      width: sidebarWidth.collapsed,
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
    "@media print": {
      display: "none",
    },
  },
  drawerPaper: {
    width: "inherit",
    maxHeight: newSidebarDesign ?
      `calc(100vh - ${theme.mixins.toolbar.minHeight}px)` :
      `calc(100% - ${theme.palette.layout.sidebar.overviewOffset})`,
    height: newSidebarDesign ? `calc(100vh - ${theme.mixins.toolbar.minHeight}px)` : undefined,
    marginTop: newSidebarDesign ? `${theme.mixins.toolbar.minHeight}px` : undefined,
    [theme.breakpoints.up("sm")]: newSidebarDesign ? {
      maxHeight: "calc(100vh - 64px)",
      height: "calc(100vh - 64px)",
      marginTop: "64px",
    } : {},
    paddingTop: newSidebarDesign ? theme.spacing(1) : undefined,
    borderRight: "none",
    boxShadow: `${theme.palette.layout.sidebar.boxShadow} !important`,
    scrollbarWidth: "thin",
    scrollbarColor: `${theme.palette.layout.scrollbar.thumb} ${theme.palette.layout.scrollbar.track}`,
    "&::-webkit-scrollbar": {
      width: "0.4rem",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.layout.scrollbar.thumb,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.layout.scrollbar.track,
    },
  },
  drawerContainer: {
    overflow: "auto",
  },
  overlay: {
    position: "fixed",
    minWidth: 200,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    zIndex: theme.zIndex.drawer + 2,
    boxShadow: theme.shadows[8],
    backgroundColor: theme.palette.background.paper,
    overflowY: "auto",
    transition: theme.transitions.create(["width", "opacity"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  parentTitle: {
    paddingLeft: theme.spacing(2),
    cursor: "default",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

export const useSidebarContentStyles = makeStyles()((theme) => ({
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  container: {
    backgroundColor: theme.palette?.draggable?.accent,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    cursor: "default",
  },
}));

export const useSidebarItemStyles = makeStyles()(
  (theme, {newSidebarDesign, isSidebarHidden, isCurrentPage, isCurrentParent, inOverlay}) => ({
    menuList: {
      "& a .MuiListItemIcon-root": {
        marginLeft: newSidebarDesign ? 0 : isSidebarHidden ? 0 : theme.spacing(2),
        minWidth: theme.spacing(5),
      },
      "& a": {
        justifyContent: "flex-start",
        paddingLeft: theme.spacing(2),
      },
    },
    item: {
      justifyContent: newSidebarDesign ?
        inOverlay ?
          "flex-start" :
          "center" :
        isSidebarHidden ?
          "center" :
          undefined,
      paddingBottom: newSidebarDesign ? theme.spacing(0.5) : theme.spacing(1),
      paddingTop: newSidebarDesign ? theme.spacing(0.5) : theme.spacing(1),
      paddingLeft: inOverlay ? theme.spacing(2) : undefined,
      position: "relative",
      boxShadow:
        isCurrentPage || isCurrentParent ?
          `inset -3px 0 0 ${theme.palette.primary.main}` :
          undefined,
      borderRight:
        isCurrentPage && !isCurrentParent ? `3px solid ${theme.palette.primary.main}` : undefined,
      background:
        (isCurrentPage || isCurrentParent) && theme.palette.mode !== "dark" ?
          theme.palette.primary.megaLight :
          undefined,
      "&:hover": {
        background: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main} !important`,
        "& div p, & div div": {color: theme.palette.background.default},
        "& div svg, svg": {fill: theme.palette.background.default},
      },
    },
    itemText: {
      display: newSidebarDesign ? "none" : isSidebarHidden ? "none" : "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      color: theme.palette.text.primary,
    },
    overlayItemText: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      color: theme.palette.text.primary,
    },
    labelChip: {
      height: "60%",
      fontSize: "0.55rem",
      fontWeight: 600,
      marginLeft: 5,
      marginRight: -5,
      mt: 0.5,
      "& .MuiChip-label": {
        px: 0.4,
      },
    },
  }),
);

export const useSidebarIconStyles = makeStyles()((theme, {newSidebarDesign, isSidebarHidden}) => ({
  listItemIcon: {
    color: theme?.palette?.layout?.icon?.color,
    justifyContent: newSidebarDesign ? "center" : isSidebarHidden ? "center" : undefined,
    minWidth: theme.spacing(5),
  },
}));
